/**
 * useMembershipBilling — consumer hook for MembershipBillingPort.
 *
 * Reads the membershipBilling port from PortsContext, resolves the invoice list
 * via React.use() (Suspense), and exposes requestEInvoiceProposal + initiatePayment as
 * passthrough callbacks. Throws a clear error when the port slot is absent.
 *
 * CRITICAL (041): the invoice read caches its Promise in a module-level WeakMap
 * keyed by the port instance (mirrors useCases, not the older useRef pattern). A
 * component that suspends on its INITIAL mount has its fiber — and React
 * Compiler's memoization — discarded, so a useRef/in-render cache would recreate
 * the Promise every retry and loop the Suspense boundary; the out-of-React WeakMap
 * survives the retry. `port.listInvoices()` is called ONLY on a cache miss, so the
 * async tRPC reader (network + a content-free access audit per call) fires exactly
 * once per view, never a duplicate on the post-suspense retry. The cache resets
 * when the composition swaps the adapter (new port key) and is GC'd with the port.
 *
 * Fee status is intentionally NOT read here: the proto surfaces payment state
 * per-invoice (InvoiceCard renders invoice.status), so there is no overall
 * fee-status badge to back and the port carries no aggregate fee-status read.
 *
 * Only call inside components wrapped in a Suspense boundary; Profile.tsx wraps
 * the fee view in PortErrorBoundary -> Suspense.
 *
 * Distinct from the providers/useMembershipBillingPort.ts FACTORY hook: that one
 * builds the demo adapter for the composition root, this one consumes it.
 *
 * Owned by feature 026-proto-convergence-v2.
 * Contract: specs/026-proto-convergence-v2/contracts/membership-billing-port.md
 */

import React, { useContext, useSyncExternalStore } from 'react';
import { PortsContext } from '../providers/PortsContext.js';
import type {
  BillingFacts,
  EInvoiceProposalRequest,
  EInvoiceRequestOutcome,
  Invoice,
  MembershipBillingPort,
  PaymentResult,
} from './MembershipBillingPort.js';

export interface UseMembershipBillingResult {
  readonly invoices: readonly Invoice[];
  /** The 048 delivery method + the server-derived eligibility. */
  readonly billingFacts: BillingFacts;
  /** Drop both cached reads and re-render — called after every settled attempt. */
  readonly refetch: () => void;
  /**
   * RENAMED from `orderEInvoice`, which returned void so a caller could only
   * assume it worked. The outcome is now reported (048).
   */
  readonly requestEInvoiceProposal: (
    request: EInvoiceProposalRequest,
    idempotencyKey: string,
  ) => EInvoiceRequestOutcome | PromiseLike<EInvoiceRequestOutcome>;
  readonly initiatePayment: (invoiceId: string) => PaymentResult | PromiseLike<PaymentResult>;
}

function isPromiseLike<T>(value: T | PromiseLike<T>): value is PromiseLike<T> {
  return value !== null && typeof value === 'object' && 'then' in value;
}

interface CacheEntry<T> {
  readonly promise: PromiseLike<T>;
  status: 'pending' | 'fulfilled' | 'rejected';
  value?: T;
  reason?: unknown;
}

/**
 * The last value each read resolved to, per port. While a RE-read is in flight
 * (after `invalidateMembershipBilling`) the hook serves this instead of
 * suspending: suspending would swap the whole section for the Suspense fallback,
 * hiding the outcome message the member was just shown and dropping keyboard
 * focus to <body>. The first mount still suspends — there is nothing to show.
 */
type LastValues<T> = WeakMap<MembershipBillingPort, T>;

type Read<T> =
  | { readonly sync: true; readonly value: T }
  | { readonly sync: false; readonly entry: CacheEntry<T> };

/**
 * Consult the survival cache FIRST and call the port ONLY on a miss (mirrors
 * useCases.ts): the async tRPC reader is network-backed and each call writes a
 * content-free access audit, so an unconditional per-render call would fire a
 * duplicate read + audit on the post-suspense retry render (the fiber is
 * discarded when the initial mount suspends). The synchronous demo/test path
 * returns without caching (never network, never audited).
 */
function startRead<T>(
  cache: WeakMap<MembershipBillingPort, CacheEntry<T>>,
  last: LastValues<T>,
  port: MembershipBillingPort,
  read: () => T | PromiseLike<T>,
): Read<T> {
  const cached = cache.get(port);
  if (cached !== undefined) return { sync: false, entry: cached };
  const valueOrPromise = read();
  if (!isPromiseLike(valueOrPromise)) return { sync: true, value: valueOrPromise };
  const entry: CacheEntry<T> = { promise: valueOrPromise, status: 'pending' };
  const servedStale = last.has(port);
  valueOrPromise.then(
    (value) => {
      entry.status = 'fulfilled';
      entry.value = value;
      // A read superseded by an invalidation (e.g. one in flight before a
      // write) must not overwrite the last-known value with pre-write state.
      if (cache.get(port) === entry) last.set(port, value);
      // A re-read served stale never suspended, so nothing else re-renders us.
      if (servedStale) notifyGeneration();
    },
    (reason: unknown) => {
      entry.status = 'rejected';
      entry.reason = reason;
      if (servedStale) notifyGeneration();
    },
  );
  cache.set(port, entry);
  return { sync: false, entry };
}

/** Resolve a started read. `React.use` is deliberately conditional (React permits it). */
function settleRead<T>(
  read: Read<T>,
  cache: WeakMap<MembershipBillingPort, CacheEntry<T>>,
  last: LastValues<T>,
  port: MembershipBillingPort,
): T {
  if (read.sync) return read.value;
  const { entry } = read;
  // Settled: hand the value back directly — `React.use` on a promise React has
  // not tracked would suspend for a tick even though it is already resolved.
  if (entry.status === 'fulfilled') return entry.value as T;
  if (entry.status === 'pending' && last.has(port)) return last.get(port) as T;
  if (entry.status === 'rejected') {
    // Deferred eviction (the useCases/useMyData rationale): a permanently-cached
    // rejected promise would brick the surface on every later mount, but eager
    // eviction makes React's synchronous error-retry re-fetch in an unbounded
    // loop. queueMicrotask lands the eviction AFTER React commits the boundary
    // fallback — retries still observe the rejected entry; a later mount finds
    // an empty cache and re-fetches.
    queueMicrotask(() => {
      if (cache.get(port) === entry) cache.delete(port);
    });
    // Throw the recorded reason rather than `React.use` it: a re-read that was
    // served stale was never tracked by React, so `use` would SUSPEND on it
    // (flashing the fallback, and — with the eviction above — re-fetching in a
    // loop) instead of reaching the PortErrorBoundary.
    throw entry.reason;
  }
  return React.use(entry.promise);
}

const invoicesPromiseCache = new WeakMap<MembershipBillingPort, CacheEntry<readonly Invoice[]>>();
const lastInvoices: LastValues<readonly Invoice[]> = new WeakMap();

// 048: the billing facts ride the same upstream response as the invoices, so
// they get their own entry under the same discipline. A global version counter
// (subscribed via useSyncExternalStore) re-renders every consumer after a
// mutation settles and both entries are dropped — the surface keeps the section
// mounted, so without it a re-render would resolve the already-cached promise
// and show the pre-request state.
const factsPromiseCache = new WeakMap<MembershipBillingPort, CacheEntry<BillingFacts>>();
const lastFacts: LastValues<BillingFacts> = new WeakMap();
const generationListeners = new Set<() => void>();
let generationVersion = 0;

function subscribeGeneration(callback: () => void): () => void {
  generationListeners.add(callback);
  return () => generationListeners.delete(callback);
}
function getGenerationVersion(): number {
  return generationVersion;
}

/**
 * Drop both cached reads for `port` and re-render every consumer.
 *
 * Called after EVERY settled request attempt — including the undetermined ones.
 * That is the whole recovery story for a lost response: the authoritative state
 * lives upstream, so the surface re-reads rather than guessing.
 */
export function invalidateMembershipBilling(port: MembershipBillingPort): void {
  invoicesPromiseCache.delete(port);
  factsPromiseCache.delete(port);
  notifyGeneration();
}

function notifyGeneration(): void {
  generationVersion += 1;
  for (const listener of generationListeners) listener();
}

export function useMembershipBilling(): UseMembershipBillingResult {
  // Re-render when a settled request invalidates the reads.
  useSyncExternalStore(subscribeGeneration, getGenerationVersion, getGenerationVersion);
  const ports = useContext(PortsContext);

  if (ports === null) {
    throw new Error(
      'useMembershipBilling: PortsContext not found. Make sure the component is wrapped in a PortsProvider.',
    );
  }

  if (ports.membershipBilling === undefined) {
    throw new Error(
      'useMembershipBilling: membershipBilling port is not registered. This feature requires the membership billing port to be configured.',
    );
  }

  const port: MembershipBillingPort = ports.membershipBilling;

  // Both reads go through the module-level survival cache (see `startRead` and
  // the WeakMap comment above for the Suspense-loop rationale).
  //
  // 048: BOTH reads are issued before either is awaited. `React.use` on a
  // pending promise suspends the render, so awaiting the invoices first would
  // never reach `getBillingFacts()` until the retry render — by which time the
  // reader's shared in-flight query has settled and a SECOND upstream read (and
  // access audit) goes out, with the two halves read at different instants.
  const invoicesRead = startRead(invoicesPromiseCache, lastInvoices, port, () =>
    port.listInvoices(),
  );
  const factsRead = startRead(factsPromiseCache, lastFacts, port, () => port.getBillingFacts());
  const invoices: readonly Invoice[] = settleRead(
    invoicesRead,
    invoicesPromiseCache,
    lastInvoices,
    port,
  );
  const billingFacts: BillingFacts = settleRead(factsRead, factsPromiseCache, lastFacts, port);

  return {
    invoices,
    billingFacts,
    refetch: () => invalidateMembershipBilling(port),
    requestEInvoiceProposal: (request, idempotencyKey) =>
      port.requestEInvoiceProposal(request, idempotencyKey),
    initiatePayment: (invoiceId) => port.initiatePayment(invoiceId),
  };
}
